import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersService, UserCreateByAdmin, UserUpdate } from '@/services/users';
import { ModulePermission } from '@/types';

export const useUsers = (includeInactive = false) => {
  return useQuery({
    queryKey: ['users', { includeInactive }],
    queryFn: () => usersService.getUsers(includeInactive),
  });
};

export const useUser = (id: number | null) => {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => usersService.getUser(id!),
    enabled: !!id,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserCreateByAdmin) => usersService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UserUpdate }) =>
      usersService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => usersService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useReactivateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => usersService.reactivateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const usePermissionsConfig = () => {
  return useQuery({
    queryKey: ['permissions-config'],
    queryFn: () => usersService.getPermissionsConfig(),
  });
};

export const useUpdateUserPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, permissions }: { id: number; permissions: ModulePermission[] }) =>
      usersService.updateUserPermissions(id, permissions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useResetUserPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => usersService.resetUserPermissions(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};
